import type { ApiErrorResponse } from '@promtver/contracts';

import { ApiError, NETWORK_ERROR_CODE } from './errors';
import { clearSession, getSession, setSession, type StoredSession } from './tokenStore';

/**
 * HTTP-клиент панели администратора (дублируется в каждом приложении —
 * общий пакет HTTP-клиента в этой версии не предусмотрен).
 *
 * Bearer-токен, автоматическое обновление по refresh-токену при 401,
 * единый формат ошибок, курсорная пагинация (ТЗ §4.3, §7.3).
 */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/+$/, '');

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  auth?: boolean;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = path.startsWith('http') ? path : `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

function toApiError(status: number, body: unknown): ApiError {
  const parsed = body as ApiErrorResponse | undefined;
  const error = parsed?.error;
  if (error && typeof error.code === 'string') {
    return new ApiError(error.code, error.message ?? '', status, error.details);
  }
  return new ApiError('unexpected_error', '', status);
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const session = getSession();
  if (!session?.refreshToken) return false;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(buildUrl('/auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        });
        if (!response.ok) {
          clearSession();
          return false;
        }
        const body = (await response.json()) as Partial<StoredSession> & { expiresIn?: number };
        if (!body.accessToken || !body.refreshToken) {
          clearSession();
          return false;
        }
        setSession({
          accessToken: body.accessToken,
          refreshToken: body.refreshToken,
          expiresAt: Date.now() + (body.expiresIn ?? 3600) * 1000,
          user: body.user ?? session.user,
        });
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
}

async function performRequest(path: string, options: RequestOptions, withAuth: boolean): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...(options.headers ?? {}) };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (withAuth) {
    const session = getSession();
    if (session?.accessToken) {
      headers.Authorization = `Bearer ${session.accessToken}`;
    }
  }

  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new ApiError(NETWORK_ERROR_CODE, 'Сервер недоступен', 0);
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const withAuth = options.auth !== false;
  let response = await performRequest(path, options, withAuth);

  if (response.status === 401 && withAuth) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await performRequest(path, options, withAuth);
    } else {
      clearSession();
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    throw toApiError(response.status, body);
  }

  return body as T;
}

export function apiBaseUrl(): string {
  return API_BASE_URL;
}
